<template>
  <div class="segment container">
    <div v-if="order && order.uuid">
      <!-- Botones de acción arriba -->
      <div class="is-flex is-justify-content-flex-end mb-3">
        <div class="buttons are-small">
          <button
            class="button is-info"
            @click="comment"
            :title="$t('comment')"
          >
            <span class="icon"><i class="fa fa-commenting"></i></span>
          </button>
          <button
            class="button is-success"
            @click="approve"
            :title="$t('approve')"
          >
            <span class="icon"><i class="fa fa-check"></i></span>
          </button>
          <button
            class="button is-danger"
            @click="reject"
            :title="$t('reject')"
          >
            <span class="icon"><i class="fa fa-times"></i></span>
          </button>
          <button
            class="button is-warning"
            @click="previous"
            :title="$t('previous')"
          >
            <span class="icon"><i class="fa fa-angle-left"></i></span>
          </button>
          <button class="button is-warning" @click="next" :title="$t('next')">
            <span class="icon"><i class="fa fa-angle-right"></i></span>
          </button>
        </div>
      </div>

      <!-- Input de búsqueda y número de orden -->
      <div class="is-flex is-align-items-center mb-3">
        <label class="label mr-2 mb-0">{{ $t("order number") }}</label>
        <input
          class="input is-small mr-2"
          style="width: 110px"
          type="text"
          v-model.number="order_number"
          @keyup.enter="load_order"
        />
        <button class="button is-primary is-small" @click="load_order">
          {{ $t("go") }}
        </button>
        <span class="tag is-medium ml-4">
          {{ $t("current order") }}:
          <strong class="has-text-primary ml-2">
            {{ order.order_number }}</strong
          >
        </span>
      </div>

      <!-- Mensaje de validación -->
      <article v-if="order.is_validated" class="message is-warning mb-3">
        <div class="message-header">
          {{ $t("this order has been already validated") }}
        </div>
        <div class="message-body">
          <p>
            {{ $t("result") }}:
            <strong>{{ $t(order.validation_result.result) }}</strong>
          </p>
          <p v-if="order.validation_result.reason">
            {{ $t("reason") }}: <em>{{ order.validation_result.reason }}</em>
          </p>
        </div>
      </article>

      <!-- Comentarios -->
      <article
        v-if="
          order.settings &&
          order.settings.comments &&
          order.settings.comments.length
        "
        class="message is-info mb-3"
      >
        <div class="message-header">
          {{ $t("comments") }}
        </div>
        <div class="message-body">
          <div
            v-for="(comment, cidx) in order.settings.comments"
            :key="cidx"
            class="box mb-2 py-2 px-3 is-flex is-justify-content-space-between is-align-items-center"
            style="font-size: 0.95em"
          >
            <div>
              <div class="has-text-weight-semibold">{{ comment.comment }}</div>
              <div class="has-text-grey is-size-7">
                {{ format_date(comment.create_date) }}
              </div>
            </div>
            <button
              class="delete"
              @click="delete_comment(comment)"
              :title="$t('delete')"
            ></button>
          </div>
        </div>
      </article>

      <!-- Detalles de la orden y pagos en dos columnas -->
      <div class="columns is-gapless">
        <div class="column is-7">
          <div class="box py-3 px-4 mb-2">
            <div class="title is-5 mb-1">{{ order.name }}</div>
            <div class="is-flex is-align-items-center mb-1">
              <span class="icon is-small mr-1"
                ><i class="fa fa-envelope"></i
              ></span>
              <span class="is-size-7">{{ order.email }}</span>
            </div>
            <div class="is-flex is-align-items-center mb-1">
              <span class="icon is-small mr-1"
                ><i class="fa fa-phone"></i
              ></span>
              <span class="is-size-7">{{ order.phone }}</span>
            </div>
            <div class="is-flex is-align-items-center mb-2">
              <span class="icon is-small mr-1"
                ><i class="fa fa-graduation-cap"></i
              ></span>
              <span class="is-size-7">{{ order.grade }}</span>
            </div>
            <div class="title is-6 mb-2 mt-3">{{ $t("order detail") }}</div>
            <table class="table is-narrow is-fullwidth mb-0">
              <thead>
                <tr>
                  <th>{{ $t("quantity") }}</th>
                  <th>{{ $t("product") }}</th>
                  <th>{{ $t("size") }}</th>
                  <th>{{ $t("price") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(item, idx) in order.tailoring_order_items"
                  :key="idx"
                >
                  <td>{{ item.quantity }}</td>
                  <td>{{ item.product.name }}</td>
                  <td>{{ item.size }}</td>
                  <td>{{ format_currency(item.price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <div class="column is-5">
          <div class="box py-3 px-4 mb-2" style="min-height: 100%">
            <div class="title is-6 mb-2">{{ $t("payment") }}</div>
            <div
              v-if="
                order.tailoring_order_payments &&
                order.tailoring_order_payments.length
              "
            >
              <div
                v-for="(payment, pidx) in order.tailoring_order_payments"
                :key="pidx"
                class="mb-3"
              >
                <div
                  class="is-flex is-justify-content-space-between is-align-items-center mb-1"
                >
                  <span class="has-text-weight-semibold">{{
                    format_currency(payment.amount)
                  }}</span>
                  <span class="is-size-7">{{ format_date(payment.date) }}</span>
                </div>
                <div class="is-size-7 mb-1">
                  <span>{{ $t(payment.type) }}</span>
                  <span v-if="payment.bank"> - {{ payment.bank }}</span>
                </div>
                <div class="is-size-7 mb-1">
                  <span v-if="payment.receipt_number">
                    {{ $t("receipt") }}
                    <strong>{{ payment.receipt_number }}</strong>
                  </span>
                  <span v-if="payment.document_number" class="ml-2">
                    {{ $t("document number") }}
                    <strong>{{ payment.document_number }}</strong>
                  </span>
                </div>
                <div v-if="payment.document_file">
                  <div
                    v-if="
                      payment.document_file.settings.mimetype.includes('image')
                    "
                  >
                    <div class="buttons is-centered mb-2">
                      <button
                        class="button is-small"
                        :title="$t('rotate left')"
                        @click="rotate_left"
                      >
                        <span class="icon"
                          ><i class="fa fa-rotate-left"></i
                        ></span>
                      </button>
                      <button
                        class="button is-small"
                        :title="$t('rotate right')"
                        @click="rotate_right"
                      >
                        <span class="icon"
                          ><i class="fa fa-rotate-right"></i
                        ></span>
                      </button>
                    </div>
                    <div class="zoomer_wrapper mb-2">
                      <vue-image-zoomer
                        :regular="`${api_host}/drive/static/${payment.document_file.settings.final_name}`"
                        :zoom="`${api_host}/drive/static/${payment.document_file.settings.final_name}`"
                        :zoom-amount="3"
                        @on-zoom="onZoom"
                        :click-zoom="true"
                      />
                    </div>
                  </div>
                  <article class="message is-warning" v-else>
                    <div class="message-body">
                      <button
                        class="button is-primary is-pulled-right"
                        @click="download_payment_document(payment)"
                      >
                        <span class="icon"><i class="fa fa-download"></i></span>
                        <span>{{ $t("download") }}</span>
                      </button>
                      <div class="title is-6">
                        {{ payment.document_file.settings.mimetype }}
                      </div>
                      <div class="subtitle is-7">
                        {{
                          translate_file_size(
                            payment.document_file.settings.size
                          )
                        }}
                      </div>
                    </div>
                  </article>
                </div>
                <article class="message is-warning" v-else>
                  <div class="message-body">
                    {{ $t("no document provided") }}
                  </div>
                </article>
              </div>
            </div>
            <article class="message is-warning" v-else>
              <div class="message-body">
                {{ $t("no payment registered") }}
              </div>
            </article>
          </div>
        </div>
      </div>
    </div>
    <article class="message is-success" v-else>
      <div class="message-body">
        <span class="icon">
          <i class="fa fa-check"></i>
        </span>
        {{ $t("no more orders to validate") }}
      </div>
    </article>
  </div>
</template>

<script>
import {
  get,
  error_message,
  del,
  format_currency,
  format_date,
  input_alert,
  success_message,
  api_host,
  warning_message,
  post,
  confirm_deletion_dialog,
  translate_file_size,
} from "@/utils";

import { VueImageZoomer } from "vue-image-zoomer";
import "vue-image-zoomer/dist/style.css";

export default {
  data() {
    return {
      currentPage: 1,
      totalPages: 3,
      inputPage: null,
      order: {},
      order_number: null,
      current_angle: 0,
    };
  },
  components: {
    VueImageZoomer,
  },
  computed: {
    api_host() {
      return api_host;
    },
  },
  methods: {
    format_currency,
    translate_file_size,
    format_date,
    load_order() {
      let v = this;
      v.$root.loader(true);
      get("/tailoring_order/validator", { number: v.order_number })
        .then((response) => {
          v.order = response;
        })
        .catch((error) => {
          console.error(error);
          error_message(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    fetch_next(from_order_id) {
      let v = this;
      v.order = {};
      v.$root.loader(true);
      get("/tailoring_order/validator/next", { from: from_order_id })
        .then((response) => {
          v.order = response;
          v.current_angle = 0;
        })
        .catch((error) => {
          if (from_order_id) {
            if (error.status == 404) v.fetch_next();
            else error_message(error);
          } else {
            if (error.status != 404) error_message(error);
          }
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    refresh_order() {
      let v = this;
      v.$root.loader(true);
      get("/tailoring_order/validator/uuid", { uuid: v.order.uuid })
        .then((response) => {
          v.order = response;
          v.current_angle = 0;
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    approve() {
      let v = this;
      v.$root.loader(true);
      post("/tailoring_order/validator/approve", { uuid: v.order.uuid })
        .then((response) => {
          v.fetch_next(v.order.order_id);
        })
        .catch((error) => {
          console.error(error);
          error_message(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    reject() {
      let v = this;
      input_alert({
        title: v.$t("rejection"),
        input_label: v.$t("detail your rejection reason"),
        input: "textarea",
      })
        .then((result) => {
          v.$root.loader(true);
          post("/tailoring_order/validator/reject", {
            uuid: v.order.uuid,
            reason: result,
          })
            .then((response) => {
              v.fetch_next(v.order.order_id);
            })
            .catch((error) => {
              console.error(error);
              error_message(error);
            })
            .finally(() => {
              v.$root.loader(false);
            });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    next() {
      let v = this;
      v.fetch_next(v.order.order_id);
    },
    previous() {
      let v = this;
      let _order = { ...v.order };
      v.order = {};
      v.$root.loader(true);
      get("/tailoring_order/validator/previous", { from: _order.order_id })
        .then((response) => {
          v.order = response;
          v.current_angle = 0;
        })
        .catch((error) => {
          if (error.status == 404) v.fetch_next();
          else error_message(error);
        })
        .finally(() => {
          v.$root.loader(false);
        });
    },
    download_payment_document(payment) {
      let final_name_has_extension =
        payment.document_file.settings.final_name.includes(".");
      if (final_name_has_extension) {
        let url = `${api_host}/drive/static/${payment.document_file.settings.final_name}`;
        window.open(url, "_blank");
        return;
      } else {
        let mimetype = payment.document_file.settings.mimetype;
        let download_extension = "";
        if (mimetype == "application/pdf") download_extension = "pdf";
        else if (mimetype == "image/jpeg") download_extension = "jpg";
        else if (mimetype == "image/png") download_extension = "png";
        else if (mimetype == "text/plain") download_extension = "txt";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        )
          download_extension = "docx";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        )
          download_extension = "xlsx";
        else if (
          mimetype ==
          "application/vnd.openxmlformats-officedocument.presentationml.presentation"
        )
          download_extension = "pptx";
        else if (mimetype == "application/vnd.ms-excel")
          download_extension = "xls";
        else if (mimetype == "application/msword") download_extension = "doc";

        let url = `${api_host}/drive/static/${payment.document_file.settings.final_name}`;
        let download_name = `payment_receipt_order_${payment.order_id}.${download_extension}`;
        fetch(url)
          .then((response) => {
            return response.blob();
          })
          .then((blob) => {
            let url = window.URL.createObjectURL(blob);
            let a = document.createElement("a");
            a.href = url;
            a.download = download_name;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
          })
          .catch((error) => {
            console.error(error);
          });
      }
    },
    rotate_left() {
      let v = this;
      // let img = document.getElementById("payment_image");
      let img = window.jQuery(".zoomer_wrapper img")[0];
      let angle = v.current_angle - 90;
      img.style.transform = `rotate(${angle}deg)`;
      v.current_angle = angle;
    },
    rotate_right() {
      let v = this;
      // let img = document.getElementById("payment_image");
      let img = window.jQuery(".zoomer_wrapper img")[0];
      let angle = v.current_angle + 90;
      img.style.transform = `rotate(${angle}deg)`;
      v.current_angle = angle;
    },
    comment() {
      let v = this;
      input_alert({
        title: v.$t("comment"),
        input_label: v.$t("detail your comment on this order"),
        input: "textarea",
      })
        .then((result) => {
          v.$root.loader(true);
          post("/tailoring_order/validator/comment", {
            uuid: v.order.uuid,
            comment: result,
          })
            .then((response) => {
              v.refresh_order();
            })
            .catch((error) => {
              console.error(error);
              error_message(error);
            })
            .finally(() => {
              v.$root.loader(false);
            });
        })
        .catch((error) => {
          console.error(error);
        });
    },
    delete_comment(comment) {
      let v = this;
      confirm_deletion_dialog(
        v.$t("are you sure to delete comment?"),
        v,
        () => {
          v.$root.loader(true);
          del("/tailoring_order/validator/comment", {
            uuid: v.order.uuid,
            comment_uuid: comment.uuid,
          })
            .then((stdout) => {
              v.refresh_order();
            })
            .catch((error) => {
              error_message(error);
            })
            .finally(() => {
              v.$root.loader(false);
            });
        }
      );
    },
    onZoom() {
      // let v = this;
      // setTimeout(() => {
      //   try {
      //       let img = window.jQuery(".zoomer_wrapper img")[1];
      //       console.log(img);
      //       if (!img) return;
      //       img.style.transform = `rotate(${v.current_angle}deg)`;
      //   } catch (error) {
      //     console.error(error);
      //   }
      // }, 1000);
    },
  },
  mounted() {
    let v = this;
    v.fetch_next();
  },
};
</script>

<style scoped>
.segment.container {
  max-width: 1100px;
  margin: 0 auto;
  padding: 1.5rem 0.5rem;
}
.box {
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(44, 62, 80, 0.08);
  margin-bottom: 0.5rem;
}
.table th,
.table td {
  vertical-align: middle !important;
  padding: 0.35em 0.5em;
  font-size: 0.97em;
}
.zoomer_wrapper {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #e1e8ed;
  background: #fafbfc;
  margin-bottom: 0.5rem;
}
.buttons .button {
  margin-right: 0.3rem;
}
.buttons .button:last-child {
  margin-right: 0;
}
.message-header {
  border-radius: 10px 10px 0 0;
  font-size: 1em;
}
.message-body {
  border-radius: 0 0 10px 10px;
  font-size: 0.97em;
}
</style>
